import React from 'react';
import { LuCheck, LuCircleX, LuClock, LuEqualApproximately } from 'react-icons/lu';

import { PortfolioEngineAccount, TradingStatus } from '@ndvr/portfolio-api';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency } from 'utils/number-formatter';

export interface AssessmentBadgeProps {
    label: React.ReactNode;
    className: string;
    icon: React.ReactNode;
    ariaLabel?: string;
}

export const AssessmentBadge = ({ label, className, icon, ariaLabel }: AssessmentBadgeProps) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded text-sm ${className}`}
                aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
            >
                {icon}
            </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
    </Tooltip>
);

export const StableBadge = () => (
    <AssessmentBadge label="Stable" className="bg-emerald-50 text-emerald-700" icon={<LuCheck />} />
);

export const idleCashLabel = (unencumberedCash?: string): string =>
    unencumberedCash != null ? `Idle cash: ${formatCurrency(unencumberedCash)}` : 'Idle cash';

export const insufficientCashLabel = (feeShortfall?: string, withdrawalShortfall?: string): string => {
    const feeNum = Number(feeShortfall ?? 0);
    const withdrawalNum = Number(withdrawalShortfall ?? 0);
    const total = feeShortfall != null || withdrawalShortfall != null ? feeNum + withdrawalNum : null;
    if (total == null) return 'Insufficient cash';
    const sources = [feeNum > 0 ? 'fees' : null, withdrawalNum > 0 ? 'withdrawals' : null]
        .filter(Boolean)
        .join(' and ');
    return `${formatCurrency(total)} shortfall on upcoming ${sources || 'fees and withdrawals'}`;
};

export const IdleCashBadge = ({ unencumberedCash }: { unencumberedCash?: string }) => (
    <AssessmentBadge
        label={idleCashLabel(unencumberedCash)}
        ariaLabel="Idle cash"
        className="bg-lime-50 text-green-600"
        icon={<LuClock />}
    />
);

export const InsufficientCashBadge = ({
    feeShortfall,
    withdrawalShortfall,
}: {
    feeShortfall?: string;
    withdrawalShortfall?: string;
}) => (
    <AssessmentBadge
        label={insufficientCashLabel(feeShortfall, withdrawalShortfall)}
        ariaLabel="Insufficient cash"
        className="bg-orange-50 text-amber-800"
        icon={<LuCircleX />}
    />
);

export const TaxLossHarvestingBadge = ({
    harvestableLoss,
    harvestableSecurityCount,
    harvestableProceeds,
}: {
    harvestableLoss?: string;
    harvestableSecurityCount?: number;
    harvestableProceeds?: string;
}) => {
    const hasDetail = harvestableLoss != null || harvestableSecurityCount != null || harvestableProceeds != null;
    const label = hasDetail ? (
        <div className="flex flex-col gap-0.5">
            <span className="font-medium">TLH opportunity</span>
            {harvestableLoss != null && <span>{formatCurrency(harvestableLoss)} of harvestable losses</span>}
            {(harvestableSecurityCount != null || harvestableProceeds != null) && (
                <span className="text-xs opacity-80">
                    {[
                        harvestableSecurityCount != null
                            ? `${harvestableSecurityCount} position${harvestableSecurityCount === 1 ? '' : 's'}`
                            : null,
                        harvestableProceeds != null
                            ? `${formatCurrency(harvestableProceeds)} in total sell proceeds`
                            : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
            )}
        </div>
    ) : (
        'Tax loss harvesting'
    );
    return (
        <AssessmentBadge
            label={label}
            ariaLabel="Tax loss harvesting"
            className="bg-blue-50 text-blue-700"
            icon={<LuEqualApproximately />}
        />
    );
};

export const AccountAssessmentBadges = ({ account }: { account: PortfolioEngineAccount }) => {
    if (account.tradingStatus === TradingStatus.REBALANCE_IN_PROGRESS) return null;

    const { hasIdleCash, hasInsufficientCash, hasTaxLossHarvesting } = account;
    const hasAnyFlag = hasIdleCash || hasInsufficientCash || hasTaxLossHarvesting;
    return (
        <div className="flex items-center gap-1">
            {!hasAnyFlag && <StableBadge />}
            {hasIdleCash && <IdleCashBadge unencumberedCash={account.unencumberedCash} />}
            {hasInsufficientCash && (
                <InsufficientCashBadge
                    feeShortfall={account.feeShortfall}
                    withdrawalShortfall={account.withdrawalShortfall}
                />
            )}
            {hasTaxLossHarvesting && (
                <TaxLossHarvestingBadge
                    harvestableLoss={account.harvestableLoss}
                    harvestableSecurityCount={account.harvestableSecurityCount}
                    harvestableProceeds={account.harvestableProceeds}
                />
            )}
        </div>
    );
};
